import qs from 'qs'
import request from 'api/requestUtils'
import type { actions } from '@luxuryescapes/contract-svc-tag'

type GetManyTagsResponse = actions['getManyTags']['responses']['200']['content']['application/json']

type GetManyTagAssignmentsResponse = actions['getManyTagAssignments']['responses']['200']['content']['application/json']

export type TagAssignmentEntityType = GetManyTagAssignmentsResponse['result'][0]['entityType']

export interface GetTagsByDestinationIdParams {
  destinationId: string
  limit?: number
  offset?: number
}

export interface GetTagsByOfferIdParams {
  offerId: string
  limit?: number
  offset?: number
}

export interface GetTagAssignmentsParams {
  tagId: string
  entityId?: string
  entityType?: TagAssignmentEntityType
}

export interface TagAssignment {
  id: string
  tagId: string
  entityId: string
  entityType: TagAssignmentEntityType
}

export interface Tag {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface GetTagsResult {
  tags: Array<Tag>
  pagination: {
    limit: number
    offset: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

/**
 * Retrieves tags assigned to a destination by destination ID
 * @param params - Parameters including destinationId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByDestinationId(
  params: GetTagsByDestinationIdParams,
): Promise<GetTagsResult> {
  const { destinationId, limit, offset } = params

  const query = qs.stringify({
    entityIdIn: [destinationId],
    entityTypeIn: ['DESTINATION'],
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`, { excludeBrand: true })
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}

/**
 * Retrieves tags assigned to an offer by offer ID
 * @param params - Parameters including offerId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByOfferId(
  params: GetTagsByOfferIdParams,
): Promise<GetTagsResult> {
  const { offerId, limit, offset } = params

  const query = qs.stringify({
    entityIdIn: [offerId],
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`, { excludeBrand: true })
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}

/**
 * Retrieves tag assignments assigned to an tag id
 * @param params - Parameters including tagId, entityId, entityType
 * @returns Promise with tag assignments
 */
export function getTagAssignments(
  params: GetTagAssignmentsParams,
): Promise<Array<TagAssignment>> {
  const { tagId, entityId, entityType } = params

  const query = qs.stringify({
    tagId,
    ...(entityId !== undefined && { entityId }),
    ...(entityType !== undefined && { entityType }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagAssignmentsResponse>(`/api/tags/v1/tag-assignments?${query}`, { excludeBrand: true })
    .then((response) => response.result)
}
